import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Card,
  Button,
  Select,
  DatePicker,
  Table,
  Tag,
  Tabs,
  Space,
  Typography,
  Spin,
  Row,
  Col,
  Alert,
} from 'antd';
import {
  FileTextOutlined,
  EditOutlined,
  EyeOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/auth';
import {
  getReports,
  getTeamReports,
  getCurrentWeekStatus,
  getAssignableUsers,
} from '../lib/api';
import { getUserDisplayName } from '../lib/user';
import type { ColumnType } from 'antd/es/table';

const { Title, Text } = Typography;

interface Report {
  id: number;
  weekStart: string;
  status: 'draft' | 'submitted' | 'reviewed';
  updatedAt: string;
}

interface TeamReport extends Report {
  user: {
    id: number;
    name: string | null;
    username: string | null;
    email: string | null;
  };
}
const { Option } = Select;

export default function ReportsPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [myReports, setMyReports] = useState<any[]>([]);
  const [teamReports, setTeamReports] = useState<any[]>([]);
  const [currentStatus, setCurrentStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my');
  const [employees, setEmployees] = useState<any[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterWeekStart, setFilterWeekStart] = useState<string>('');
  const [filterEmployee, setFilterEmployee] = useState<string>('');

  useEffect(() => {
    loadReports();
  }, [filterStatus, filterWeekStart, filterEmployee, activeTab]);

  useEffect(() => {
    if (activeTab === 'team' && user?.role !== 'employee') {
      getAssignableUsers()
        .then((users) => setEmployees(users))
        .catch(console.error);
    }
  }, [activeTab, user]);

  const loadReports = async () => {
    try {
      const myFilters: { weekStart?: string; status?: string } = {};
      if (filterWeekStart) myFilters.weekStart = filterWeekStart;
      if (filterStatus) myFilters.status = filterStatus;

      const [myData, currentData] = await Promise.all([
        getReports(myFilters),
        getCurrentWeekStatus(),
      ]);
      setMyReports(myData);
      setCurrentStatus(currentData);

      if (user?.role !== 'employee') {
        const teamFilters: { weekStart?: string; status?: string; userId?: number } =
          {};
        if (filterWeekStart) teamFilters.weekStart = filterWeekStart;
        if (filterStatus) teamFilters.status = filterStatus;
        if (filterEmployee) teamFilters.userId = parseInt(filterEmployee);

        const teamData = await getTeamReports(teamFilters);
        setTeamReports(teamData);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterWeekStart('');
    setFilterEmployee('');
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      draft: { color: 'default', label: '草稿' },
      submitted: { color: 'processing', label: '已提交' },
      reviewed: { color: 'success', label: '已審核' },
      none: { color: 'default', label: '未建立' },
    };
    const { color, label } = config[status] || config.none;
    return <Tag color={color}>{label}</Tag>;
  };

  const handleNewReport = () => {
    navigate('/reports/new');
  };

  const myReportColumns: ColumnType<Report>[] = [
    {
      title: '週次',
      dataIndex: 'weekStart',
      key: 'weekStart',
      fixed: 'left',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('zh-TW'),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => getStatusTag(status),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      responsive: ['md'],
      render: (date) => new Date(date).toLocaleString('zh-TW'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 130,
      render: (_, record) => (
        <Space>
          {record.status === 'draft' && (
            <Link to={`/reports/new?draftId=${record.id}`}>
              <Button type="primary" size="small" icon={<EditOutlined />}>
                編輯
              </Button>
            </Link>
          )}
          <Link to={`/reports/${record.id}`}>
            <Button size="small" icon={<EyeOutlined />}>
              查看
            </Button>
          </Link>
        </Space>
      ),
    },
  ];

  const teamReportColumns: ColumnType<TeamReport>[] = [
    {
      title: '員工',
      dataIndex: 'user',
      key: 'user',
      fixed: 'left',
      width: 120,
      render: (user) => getUserDisplayName(user),
    },
    {
      title: '週次',
      dataIndex: 'weekStart',
      key: 'weekStart',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('zh-TW'),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => getStatusTag(status),
    },
    {
      title: '提交時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      responsive: ['md'],
      render: (date) => new Date(date).toLocaleString('zh-TW'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Link to={`/reports/${record.id}`}>
          <Button size="small" icon={<EyeOutlined />}>
            查看
          </Button>
        </Link>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const weekStart = currentStatus?.weekStart
    ? new Date(currentStatus.weekStart).toLocaleDateString('zh-TW')
    : '';

  const filterSection = (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={6} lg={6}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              狀態
            </label>
            <Select
              style={{ width: '100%' }}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="全部狀態"
              allowClear
            >
              <Option value="draft">草稿</Option>
              <Option value="submitted">已提交</Option>
              <Option value="reviewed">已審核</Option>
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={6} lg={6}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              週次
            </label>
            <DatePicker
              style={{ width: '100%' }}
              value={filterWeekStart ? dayjs(filterWeekStart) : null}
              onChange={(date) => setFilterWeekStart(date ? date.format('YYYY-MM-DD') : '')}
              placeholder="選擇週次"
            />
          </div>
        </Col>

        {activeTab === 'team' && user?.role !== 'employee' && (
          <Col xs={24} sm={12} md={6} lg={6}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                員工
              </label>
              <Select
                style={{ width: '100%' }}
                value={filterEmployee}
                onChange={setFilterEmployee}
                placeholder="全部員工"
                allowClear
              >
                {employees.map((emp) => (
                  <Option key={emp.id} value={emp.id}>
                    {getUserDisplayName(emp)}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
        )}

        <Col xs={24} sm={12} md={6} lg={6}>
          <div style={{ marginTop: 28 }}>
            <Button icon={<ClearOutlined />} onClick={handleClearFilters} block>
              清除篩選
            </Button>
          </div>
        </Col>
      </Row>
    </Card>
  );

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          週報管理
        </Title>
        <Button
          type="primary"
          icon={<FileTextOutlined />}
          onClick={handleNewReport}
        >
          {currentStatus?.status === 'draft' ? '繼續編輯週報' : '填寫本週週報'}
        </Button>
      </div>

      {/* Current Week Status */}
      <Alert
        title={
          <Space>
            <Text strong>本週進度</Text>
            <Text type="secondary">({weekStart} 開始)</Text>
            {getStatusTag(currentStatus?.status || 'none')}
          </Space>
        }
        type="info"
        showIcon
        style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}
      />

      {user?.role === 'employee' ? (
        <>
          {filterSection}
          <Card>
            <Table
              dataSource={myReports}
              columns={myReportColumns}
              rowKey="id"
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 筆`,
              }}
              locale={{
                emptyText: '您還沒有提交過任何週報',
              }}
            />
          </Card>
        </>
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: 'my',
              label: '我的週報',
              children: (
                <>
                  {filterSection}
                  <Card>
                    <Table
                      dataSource={myReports}
                      columns={myReportColumns}
                      rowKey="id"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 筆`,
                      }}
                      locale={{
                        emptyText: '您還沒有提交過任何週報',
                      }}
                    />
                  </Card>
                </>
              ),
            },
            {
              key: 'team',
              label: '團隊週報',
              children: (
                <>
                  {filterSection}
                  <Card>
                    <Table
                      dataSource={teamReports}
                      columns={teamReportColumns}
                      rowKey="id"
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 筆`,
                      }}
                      locale={{
                        emptyText: '本週尚未收到團隊成員的週報',
                      }}
                    />
                  </Card>
                </>
              ),
            },
          ]}
        />
      )}
    </Space>
  );
}
